import { has } from '@/commonFunctions/utils'
import { Button, ButtonPrimary } from '@/components/button'
import { MainContent } from '@/components/content'
import { ArrowBack } from '@/components/icons'
import { DatePickerInput, NumberInput, PriceInput, Select, TextInput, TimezoneSelect } from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { EVENT_MODIFY, FLEX_ELIMINATION } from '@/constants/Common'
import { bracketFormatOptions, eliminationOptions, playerGroupOptions } from '@/constants/DropdownOptionsConstants'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_EVENTS } from '@/constants/Routes'
import { timeZoneList } from '@/constants/Timezones'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { getBracketDetail, getEventDetail } from '@/pages/event/eventSlice'
import { eventSelector } from '@/pages/event/selectors'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useEditBracket } from '@/services'
import { EventFormat, PlayerGroup } from '@/types'
import editBracketValidationSchema from '@/utils/schema/EditBracketValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { getClubRoles } from '../../club/clubSlice'
import { clubSelector } from '../../club/selectors'

export type EditBracketForm = {
  name: string
  elimination: string
  format: EventFormat | string
  playerGroup: PlayerGroup | string
  minAge: string
  maxAge: string
  minRating: string
  maxRating: string
  registrationStartDateTime: string
  registrationEndDateTime: string
  competitionStartDateTime: string
  competitionEndDateTime: string
  memberPrice: string
  nonMemberPrice: string
  courts: number
  maxTeam: string
  waitList: string
  timeZone: string
  zoneName: string
}

export const EditBracket = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()
  const dispatch = useAppDispatch()
  const { isShowDropdown } = useAppSelector(appSelector)
  const { bracket, isLoading, event } = useAppSelector(eventSelector)
  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { eventId, bracketId } = params

  const [disableTeams, setDisableTeams] = useState(false)
  const [autoGenerateState, setAutoGenerateState] = useState('')

  const { mutate: editBracket } = useEditBracket()

  const defaultValues: EditBracketForm = {
    name: bracket?.name || '',
    elimination: bracket?.elimination || '',
    minAge: bracket?.ageBracket?.[0]?.toString() || '',
    maxAge: bracket?.ageBracket?.[1]?.toString() || '',
    minRating: bracket?.ratingBracket?.[0]?.toString() || '',
    maxRating: bracket?.ratingBracket?.[1]?.toString() || '',
    playerGroup: bracket?.playerGroup || '',
    format: bracket?.format || '',
    registrationStartDateTime: bracket?.registrationDateTime?.[0] || '',
    registrationEndDateTime: bracket?.registrationDateTime?.[1] || '',
    competitionStartDateTime: bracket?.durationDateTime?.[0] || '',
    competitionEndDateTime: bracket?.durationDateTime?.[1] || '',
    memberPrice: bracket?.memberFee?.toString() || '',
    nonMemberPrice: bracket?.nonMemberFee?.toString() || '',
    courts: bracket?.courts || 0,
    maxTeam: bracket?.maxTeam.toString() || '',
    waitList: bracket?.waitList.toString() || '',
    timeZone: bracket?.timeZone || '',
    zoneName: bracket?.zoneName || '',
  }

  const {
    setValue,
    control,
    handleSubmit,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditBracketForm>({
    defaultValues,
    resolver: yupResolver(editBracketValidationSchema as any),
    mode: 'onTouched',
  })

  const watchElimination = watch('elimination')
  const watchBracketType = watch('format')
  const watchPlayerGroup = watch('playerGroup')
  const watchAgeRangeMin = watch('minAge')
  const watchAgeRangeMax = watch('maxAge')
  const watchRatingRangeMin = watch('minRating')
  const watchRatingRangeMax = watch('maxRating')
  const watchName = watch('name')

  const onGoBack = () => {
    navigate(-1)
  }

  const onEliminationChange = (value: string) => {
    value &&
      setValue('elimination', value, {
        shouldDirty: true,
        shouldValidate: true,
      })

    setValue('courts', 0)
    if (watchElimination === 'ROUND_ROBIN') {
      setValue('maxTeam', '0', { shouldDirty: false, shouldValidate: false })
    } else if (watchElimination === 'FLEX') {
      setValue('maxTeam', '0', { shouldDirty: false, shouldValidate: false })
    } else if (watchElimination === 'COMPASS') {
      setValue('maxTeam', '16', { shouldDirty: false, shouldValidate: false })
    }
  }

  const autoGenerateName = () => {
    const name = `${watchPlayerGroup} ${watchBracketType} - ${watchRatingRangeMin}-${watchRatingRangeMax}:${watchAgeRangeMin}-${watchAgeRangeMax}`
    setValue('name', name, { shouldValidate: true })
    setAutoGenerateState(name)
  }

  const onResetValue = () => {
    if (bracket) {
      setValue('name', bracket?.name)
      setValue('elimination', bracket?.elimination)
      setValue('minAge', bracket.ageBracket?.[0]?.toString())
      setValue('maxAge', bracket?.ageBracket?.[1]?.toString())
      setValue('minRating', bracket?.ratingBracket?.[0]?.toString())
      setValue('maxRating', bracket?.ratingBracket?.[1]?.toString())
      setValue('playerGroup', bracket?.playerGroup)
      setValue('format', bracket?.format)
      setValue('registrationStartDateTime', bracket?.registrationDateTime?.[0])
      setValue('registrationEndDateTime', bracket?.registrationDateTime?.[1])
      setValue('competitionStartDateTime', bracket?.durationDateTime?.[0])
      setValue('competitionEndDateTime', bracket?.durationDateTime?.[1])
      setValue('memberPrice', bracket?.memberFee?.toString())
      setValue('nonMemberPrice', bracket?.nonMemberFee?.toString())
      setValue('courts', bracket?.courts)
      setValue('maxTeam', bracket?.maxTeam.toString())
      setValue('waitList', bracket?.waitList.toString())
      setValue('timeZone', bracket?.timeZone)
      setValue('zoneName', bracket?.zoneName)
    }
  }

  const onSubmit = handleSubmit(async (data) => {
    const payload = {
      bracketId: bracketId,
      leagueId: eventId,
      ageBracket: [data.minAge || null, data.maxAge || null],
      durationDateTime: [data.competitionStartDateTime || null, data.competitionEndDateTime || null],
      maxTeam: data.maxTeam || null,
      name: data.name,
      elimination: data.elimination || null,
      memberFee: data.memberPrice.toString(),
      nonMemberFee: data.nonMemberPrice.toString(),
      playerGroup: data.playerGroup || null,
      ratingBracket: [data.minRating || null, data.maxRating || null],
      courts: data.courts || null,
      timeZone: data.timeZone,
      zoneName: data.zoneName,
      registrationDateTime: [data.registrationStartDateTime || null, data.registrationEndDateTime || null],
      waitList: data.waitList,
    }
    editBracket(payload, {
      onSuccess: () => {
        navigate(-1)
        toast.success(t('common.success'))
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || APP_CUSTOM_ERROR)
      },
    })
  })

  useEffect(() => {
    if (eventId) {
      dispatch(getEventDetail(eventId))
    }

    if (bracketId) {
      dispatch(getBracketDetail(bracketId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, bracketId])

  useEffect(() => {
    onResetValue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracket])

  useEffect(() => {
    const name = `${watchPlayerGroup} ${watchBracketType} - ${watchRatingRangeMin}-${watchRatingRangeMax}:${watchAgeRangeMin}-${watchAgeRangeMax}`
    setAutoGenerateState(name)
  }, [watchBracketType, watchPlayerGroup, watchAgeRangeMin, watchAgeRangeMax, watchRatingRangeMin, watchRatingRangeMax])

  useEffect(() => {
    if (watchElimination === 'COMPASS') {
      setDisableTeams(true)
    } else {
      setDisableTeams(false)
    }
  }, [watchElimination])

  useEffect(() => {
    if (!permissions && user?.id && event) {
      dispatch(getClubRoles({ clubId: event?.clubId, userId: user?.id }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, event])

  useEffect(() => {
    if (bracket?.displayStatus === 'Completed' || !has(permissions, EVENT_MODIFY) || bracket?.hasConfirmMatch)
      navigate(`${ROUTE_EVENTS}/${eventId}/bracket/${bracketId}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracket, permissions])

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <MainContent>
          <div className="flex flex-col mb-20 lg:mb-8">
            <div className="flex items-center justify-between">
              <div className="flex gap-x-3 items-center">
                <Button onClick={onGoBack} className="hover:opacity-50">
                  <ArrowBack className="text-white" />
                </Button>
                <h1 className="text-sm md:text-xl text-white font-semibold">{t('event.editBracket')}</h1>
              </div>
              <div className="flex gap-x-4">
                <ButtonPrimary title={t('common.reset')} className="py-3 px-5" onClick={onResetValue} />
                <ButtonPrimary
                  title={t('common.saveChanges')}
                  disabled={isLoading}
                  className="py-3 px-5"
                  onClick={onSubmit}
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="bg-white rounded-2xl py-4 px-6 flex flex-col">
                <div className="flex flex-col gap-y-6 mt-6">
                  {/* Bracket Info */}
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col gap-y-2 sm:flex-row sm:gap-x-3 xl:gap-x-4">
                      <Controller
                        name="elimination"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('common.type')}
                            placeholder={t('common.select')}
                            labelClassName="!text-navy-900"
                            onChange={(value) => onEliminationChange(value)}
                            value={eliminationOptions.find((opt) => opt.value === field.value)}
                            options={eliminationOptions}
                            error={errors?.elimination?.message}
                            disabled={watchElimination === FLEX_ELIMINATION}
                          />
                        )}
                      />
                      <Controller
                        name="playerGroup"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('common.playerGroup')}
                            placeholder={t('common.select')}
                            labelClassName="!text-navy-900"
                            value={playerGroupOptions.find((opt) => opt.value === field.value)}
                            onChange={(value) => {
                              value &&
                                setValue('playerGroup', value, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              if (watchBracketType) {
                                trigger('format', { shouldFocus: false })
                              }
                            }}
                            options={playerGroupOptions}
                            error={errors?.playerGroup?.message}
                          />
                        )}
                      />
                      <Controller
                        name="format"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('common.format')}
                            placeholder={t('common.select')}
                            labelClassName="!text-navy-900"
                            value={bracketFormatOptions.find((opt) => opt.value === field.value)}
                            onChange={() => {
                              //
                            }}
                            disabled
                            options={bracketFormatOptions}
                          />
                        )}
                      />
                    </div>

                    {/* Age range */}
                    <div className="flex flex-col gap-y-2">
                      <span className="text-navy-900 text-sm font-semibold">{t('common.ageRange')}</span>
                      <div className="flex flex-col md:flex-row md:items-baseline gap-2">
                        <div className="md:flex-1 flex-col gap-y-2">
                          <NumberInput
                            label={t('common.minAge')}
                            labelClassName="!text-navy-900 !text-xs"
                            min={2}
                            {...register('minAge')}
                            error={errors?.minAge?.message}
                          />
                        </div>
                        <div className="hidden md:block translate-y-11">-</div>
                        <div className="md:flex-1 flex-col gap-y-2">
                          <NumberInput
                            label={t('common.maxAge')}
                            labelClassName="!text-navy-900 !text-xs"
                            min={2}
                            {...register('maxAge')}
                            error={errors?.maxAge?.message}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Rating range */}
                    <div className="flex flex-col gap-y-2">
                      <span className="text-navy-900 text-sm font-semibold">{t('common.ratingRange')}</span>
                      <div className="flex flex-col md:flex-row md:items-baseline gap-2">
                        <div className="md:flex-1 flex-col gap-y-2">
                          <NumberInput
                            label={t('common.minimum')}
                            labelClassName="!text-navy-900 !text-xs"
                            min={2}
                            max={8}
                            {...register('minRating')}
                            error={errors?.minRating?.message}
                          />
                        </div>
                        <div className="hidden md:block translate-y-11">-</div>
                        <div className="md:flex-1 flex-col gap-y-2">
                          <NumberInput
                            label={t('common.maximum')}
                            labelClassName="!text-navy-900 !text-xs"
                            min={2}
                            max={8}
                            {...register('maxRating')}
                            error={errors?.maxRating?.message}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Bracket name */}
                    <div className="flex flex-col gap-y-2">
                      <div className="flex flex-col md:flex-row items-center gap-2">
                        <TextInput
                          className="w-full"
                          labelClassName="!text-navy-900"
                          label={t('common.name')}
                          placeholder={t('common.bracketName')}
                          {...register('name')}
                          error={errors?.name?.message}
                        />
                        <ButtonPrimary
                          title={t('common.autoGenerate')}
                          className={`w-full md:w-auto whitespace-nowrap ${errors?.name ? 'mt-0' : 'mt-0 sm:mt-7'}`}
                          disabled={
                            !watchBracketType ||
                            !watchPlayerGroup ||
                            !watchAgeRangeMin ||
                            !watchAgeRangeMax ||
                            !watchRatingRangeMin ||
                            !watchRatingRangeMax ||
                            watchName === autoGenerateState
                          }
                          onClick={() => autoGenerateName()}
                        />
                      </div>
                    </div>

                    <NumberInput
                      label={t('common.courts')}
                      description={t('common.courtsDesc')}
                      min={1}
                      max={8}
                      labelClassName="!text-navy-900"
                      descriptionClassName="!text-navy-900"
                      disabled={!disableTeams}
                      {...register('courts')}
                      error={errors?.courts?.message}
                    />

                    <NumberInput
                      label={t('common.maxTeams')}
                      description={t('common.maxTeamsDec')}
                      min={2}
                      labelClassName="!text-navy-900"
                      descriptionClassName="!text-navy-900"
                      disabled={disableTeams}
                      {...register('maxTeam')}
                      error={errors?.maxTeam?.message}
                    />

                    <NumberInput
                      label={t('common.waitList')}
                      description={t('common.waitListDesc')}
                      min={0}
                      labelClassName="!text-navy-900"
                      descriptionClassName="!text-navy-900"
                      {...register('waitList')}
                      error={errors?.waitList?.message}
                    />
                  </div>

                  {/* Registration Date */}
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col">
                      <span className="text-navy-900 text-base font-semibold">{t('common.registration')}</span>
                      <span className="text-navy-900 text-xs">{t('common.registrationDesc')}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-x-6">
                      <div className="flex-1 flex flex-col gap-y-2">
                        <DatePickerInput
                          label={t('common.startDateTime')}
                          labelClassName="!text-navy-900"
                          selected={watch('registrationStartDateTime')}
                          showTimeInput
                          timeInputLabel="Time:"
                          dateFormat="MMM dd, yyyy h:mm aa"
                          onChange={(date: Date | null) => {
                            date &&
                              setValue('registrationStartDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                          }}
                          error={errors?.registrationStartDateTime?.message}
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-y-2">
                        <DatePickerInput
                          label={t('common.endDateTime')}
                          labelClassName="!text-navy-900"
                          selected={watch('registrationEndDateTime')}
                          showTimeInput
                          timeInputLabel="Time:"
                          dateFormat="MMM dd, yyyy h:mm aa"
                          onChange={(date: Date | null) => {
                            date &&
                              setValue('registrationEndDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                          }}
                          error={errors?.registrationEndDateTime?.message}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Competition Date */}
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col">
                      <span className="text-navy-900 text-base font-semibold">{t('common.competition')}</span>
                      <span className="text-navy-900 text-xs">{t('common.competitionDesc')} </span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-x-6">
                      <div className="flex-1 flex flex-col gap-y-2">
                        <DatePickerInput
                          label={t('common.startDateTime')}
                          labelClassName="!text-navy-900"
                          selected={watch('competitionStartDateTime')}
                          showTimeInput
                          timeInputLabel="Time:"
                          dateFormat="MMM dd, yyyy h:mm aa"
                          onChange={(date: Date | null) => {
                            date &&
                              setValue('competitionStartDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                          }}
                          error={errors?.competitionStartDateTime?.message}
                        />
                      </div>
                      <div className="flex-1 flex flex-col gap-y-2">
                        <DatePickerInput
                          label={t('common.endDateTime')}
                          labelClassName="!text-navy-900"
                          selected={watch('competitionEndDateTime')}
                          showTimeInput
                          timeInputLabel="Time:"
                          dateFormat="MMM dd, yyyy h:mm aa"
                          onChange={(date: Date | null) => {
                            date &&
                              setValue('competitionEndDateTime', moment(date).format('YYYY-MM-DDTHH:mm:ss'), {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                          }}
                          error={errors?.competitionEndDateTime?.message}
                        />
                      </div>
                    </div>

                    <Controller
                      name="timeZone"
                      control={control}
                      render={({ field }) => (
                        <TimezoneSelect
                          label={t('common.timeZone')}
                          labelClassName="!text-navy-900"
                          onBlur={field.onBlur}
                          onChange={(timeZone: string, zoneName: string) => {
                            timeZone &&
                              setValue('timeZone', timeZone, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            zoneName &&
                              setValue('zoneName', zoneName, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                          }}
                          value={timeZoneList.find((opt) => opt.value === field.value)}
                          error={errors?.timeZone?.message}
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col">
                      <span className="text-navy-900 text-base font-semibold">{t('common.price')}</span>
                      <span className="text-navy-900 text-xs">{t('common.priceDesc')}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-x-6">
                      <PriceInput
                        label={t('common.memberPrice')}
                        labelClassName="!text-navy-900"
                        {...register('memberPrice')}
                        prefix={event?.currencyDetails?.currencySymbol}
                        disabled={Number(bracket?.registeredMembers) > 0 ? bracket?.memberFee === 0 : false}
                        error={errors?.memberPrice?.message}
                      />
                      <PriceInput
                        label={t('common.nonMemberPrice')}
                        labelClassName="!text-navy-900"
                        {...register('nonMemberPrice')}
                        prefix={event?.currencyDetails?.currencySymbol}
                        disabled={Number(bracket?.registeredMembers) > 0 ? bracket?.nonMemberFee === 0 : false}
                        error={errors?.nonMemberPrice?.message}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MainContent>
      </MainLayout>
    </>
  )
}
